import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Menu, X, ChevronRight, MapPin, Mail, Phone, 
  Calendar, Users, Building2, Briefcase, GraduationCap,
  Fish, Palmtree, Ship, Anchor, Facebook, Twitter, 
  Linkedin, Instagram, Youtube, ArrowRight, Check,
  Globe, TrendingUp, Target, Award, Heart, Leaf,
  Lightbulb, CreditCard, Hotel, Car, FileText, Wifi,
  Languages, Utensils, Shield, Accessibility, BookOpen,
  Scale, ClipboardList, Map, Download, Image, Video,
  Newspaper, Camera, BadgeCheck, Clock, Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok brand icon (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const socialLinks = [
  { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61590258824373", label: "Facebook" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/gbokle-business-forum/", label: "LinkedIn" },
  { Icon: TikTokIcon, href: "https://www.tiktok.com/forumgbokle", label: "TikTok" },
];

// Form validation schema
const registrationSchema = z.object({
  full_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Adresse email invalide"),
  organization: z.string().min(2, "L'organisation doit contenir au moins 2 caractères"),
  sector: z.string().min(1, "Veuillez sélectionner un secteur"),
  participant_type: z.string().min(1, "Veuillez sélectionner votre profil"),
  message: z.string().optional(),
});

// Navigation Component
const Navigation = ({ scrolled, activeSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { id: "about", label: "À Propos" },
    { id: "leadership", label: "Leadership" },
    { id: "sectors", label: "Secteurs" },
    { id: "program", label: "Programme" },
    { id: "partnerships", label: "Partenariats" },
    { id: "practical", label: "Infos Pratiques" },
    { id: "contact", label: "Contact" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "navbar-scrolled py-3" : "navbar-transparent py-5"
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); scrollToSection("hero"); }}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img
              src="/logo-forum-white.png"
              alt="Forum Investir dans le Gbôklè"
              data-testid="navbar-logo"
              className="h-11 md:h-14 w-auto rounded-lg bg-white p-1 shadow-sm"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                data-testid={`nav-${link.id}`}
                className={`text-sm font-medium transition-colors hover:text-[#FFB400] ${
                  scrolled ? "text-[#0F172A]" : "text-white"
                } ${activeSection === link.id ? "text-[#FFB400]" : ""}`}
              >
                {link.label}
              </button>
            ))}
            <Button 
              onClick={() => scrollToSection("contact")}
              data-testid="nav-cta-button"
              className="btn-accent"
            >
              S&apos;inscrire
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-button"
            className={`md:hidden p-2 ${scrolled ? "text-[#0F172A]" : "text-white"}`}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <img
              src="/logo-forum-white.png"
              alt="Forum Investir dans le Gbôklè"
              className="h-11 w-auto"
            />
            <button onClick={() => setMobileMenuOpen(false)} data-testid="close-mobile-menu">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="mobile-menu-link w-full text-left"
              >
                {link.label}
              </button>
            ))}
          </div>
          <div className="p-4">
            <Button 
              onClick={() => scrollToSection("contact")}
              className="btn-accent w-full"
            >
              S&apos;inscrire au Forum
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <img 
        src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80" 
        alt="Côte atlantique du Gbôklè"
        className="hero-bg"
      />
      <div className="hero-overlay"></div>
      
      <div className="container-custom relative z-10 text-center text-white py-20">
        <div className="animate-fade-in-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-[#FFB400] animate-pulse"></span>
            Forum « Investir dans le Gbôklè » | 08 - 10 Octobre 2026 | Sassandra-Fresco
          </span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 animate-fade-in-up animation-delay-100">
          Forum Investir dans le
          <span className="block text-[#FFB400] mt-2">Gbôklè</span>
        </h1>
        
        <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-10 animate-fade-in-up animation-delay-200 leading-relaxed">
          <span className="text-[#FFB400] font-semibold">Terre de Culture et d&apos;Opportunités.</span> Votre porte d&apos;entrée vers
          la Riviera Atlantique Ivoirienne : 200 km de littoral, le fleuve Sassandra
          et un territoire en pleine transformation, prêt à accueillir les investisseurs.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
          <Button 
            onClick={() => scrollToSection("contact")}
            data-testid="hero-cta-primary"
            className="btn-accent text-base h-14 px-10"
          >
            Participer au Forum
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Button 
            onClick={() => scrollToSection("program")}
            data-testid="hero-cta-secondary"
            variant="outline"
            className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#082753] h-14 px-10 rounded-full"
          >
            Voir le Programme
          </Button>
        </div>

        <div className="mt-10 animate-fade-in-up animation-delay-300">
          <button
            onClick={() => scrollToSection("launch")}
            data-testid="hero-launch-banner"
            className="inline-flex flex-wrap items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-sm hover:bg-white/15 transition-colors"
          >
            <Sparkles className="w-4 h-4 text-[#FFB400]" />
            <span className="font-semibold text-[#FFB400]">Cérémonie de Lancement</span>
            <span className="text-white/85">Forum d&apos;Affaires &amp; Sêkêh Festival — 09 Juin 2026, Latrille Event (Abidjan)</span>
            <ChevronRight className="w-4 h-4 text-white/70" />
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/80 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

// Launch Ceremony Section
const LaunchCeremonySection = () => {
  const details = [
    { icon: Calendar, label: "Date", value: "09 Juin 2026" },
    { icon: Clock, label: "Heure", value: "14h00" },
    { icon: MapPin, label: "Lieu", value: "Latrille Event, Abidjan" },
    { icon: Phone, label: "Contact", value: "+225 05 06 06 55 06" },
  ];

  return (
    <section id="launch" className="section-padding bg-white" data-testid="launch-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Visual */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/launch-ceremony.jpg"
                alt="Cérémonie de Lancement Officiel — Forum d'Affaires & Sêkêh Festival"
                data-testid="launch-visual"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFB400]/15 text-[#B8860B] text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Événement à venir
            </span>
            <h2 className="section-title">
              Cérémonie de Lancement Officiel
            </h2>
            <p className="text-lg text-muted-foreground mb-3 leading-relaxed">
              Lancement officiel conjoint du <strong className="text-[#0F172A]">Forum d&apos;Affaires</strong> et
              du <strong className="text-[#0F172A]">Sêkêh Festival</strong> — un rendez-vous stratégique
              entre culture, investissement et développement durable.
            </p>
            <p className="text-[#25A18E] font-semibold mb-8">
              « Gbôklè, Terre de Culture et d&apos;Opportunités »
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {details.map((d, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-slate-50">
                  <div className="w-10 h-10 rounded-lg bg-[#082753]/10 flex items-center justify-center flex-shrink-0">
                    <d.icon className="w-5 h-5 text-[#082753]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{d.label}</p>
                    <p className="font-semibold text-[#0F172A]">{d.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-5 rounded-xl bg-[#082753]/5 border-l-4 border-[#082753]">
              <p className="text-sm text-[#475569]">
                <Calendar className="inline w-4 h-4 text-[#FFB400] mr-2 -mt-1" />
                Le Forum « Investir dans le Gbôklè » se tiendra ensuite du{" "}
                <strong className="text-[#082753]">08 au 10 octobre 2026 à Sassandra-Fresco</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "460 980", label: "Habitants (+15%)", icon: Users },
    { number: "200 km", label: "Littoral Atlantique", icon: Palmtree },
    { number: "73,68%", label: "Taux de Réalisation 2025", icon: Award },
    { number: "50 Mds+", label: "FCFA de Potentiel d'Investissement", icon: TrendingUp },
  ];

  return (
    <section className="container-custom" data-testid="stats-section">
      <div className="stats-card">
        {stats.map((stat, index) => (
          <div key={index} className="stat-item group">
            <stat.icon className="w-8 h-8 text-[#25A18E] mx-auto mb-3 group-hover:scale-110 transition-transform" />
            <div className="stat-number">{stat.number}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const objectives = [
    { icon: Target, title: "Attirer des Investissements", desc: "Mobiliser des capitaux dans 4 secteurs porteurs" },
    { icon: Globe, title: "Partenariats Durables", desc: "Alliances avec institutions financières internationales" },
    { icon: Award, title: "Valoriser le Territoire", desc: "Mettre en lumière les atouts exceptionnels du Gbôklè" },
    { icon: Building2, title: "Cadre Institutionnel", desc: "Lancement de l'Agence de Développement (ADEG)" },
  ];

  return (
    <section id="about" className="section-padding bg-white" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
              À Propos du Forum
            </span>
            <h2 className="section-title">
              Une Vision Transformatrice pour le Gbôklè
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Le Forum &quot;Investir dans le Gbôklè&quot; est l&apos;acte fondateur d&apos;une nouvelle ère 
              de développement pour la région Sassandra-Fresco. Porté par le Conseil Régional 
              et piloté par le Commissariat Général, ce forum ambitionne de transformer 
              ce territoire béni par la nature en une destination de référence pour 
              l&apos;investissement en Afrique de l&apos;Ouest.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {objectives.map((obj, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#082753]/10 flex items-center justify-center flex-shrink-0">
                    <obj.icon className="w-5 h-5 text-[#082753]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0F172A] mb-1">{obj.title}</h4>
                    <p className="text-sm text-muted-foreground">{obj.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=800&q=80" 
                alt="Conférence d'affaires"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs hidden lg:block">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#FFB400]/20 flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-[#FFB400]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#082753]">150+ km</div>
                  <div className="text-sm text-muted-foreground">de côtes atlantiques</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Leadership Section - Les Piliers du Forum
const LeadershipSection = () => {
  const presidentAchievements = [
    { icon: Building2, title: "Infrastructures Publiques", items: ["Réhabilitation du siège du Conseil Régional", "Réhabilitation de la sous-préfecture de Sassandra"] },
    { icon: Heart, title: "Santé & Urgences", items: ["Ambulances pour Grihiri, Médon et Kokolopozo", "Amélioration des évacuations sanitaires"] },
    { icon: GraduationCap, title: "Éducation 2026-2028", items: ["55 bâtiments de 3 salles de classe", "Lycée agropastoral", "Bâtiment IFEF Sassandra"] },
    { icon: Leaf, title: "Agriculture", items: ["Usine de production de riz local", "Tracteur agricole acquis", "Projet d'incubation apicole"] },
  ];

  return (
    <section id="leadership" className="section-padding bg-slate-50" data-testid="leadership-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Les Piliers du Forum
          </span>
          <h2 className="section-title">
            Un Tandem d&apos;Excellence
          </h2>
          <p className="section-subtitle mx-auto">
            Un leadership institutionnel et opérationnel réuni autour d&apos;une vision commune : 
            transformer le Gbôklè en destination économique de référence.
          </p>
        </div>

        {/* President Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-12">
          <div className="grid lg:grid-cols-5 gap-0">
            {/* President Photo/Card */}
            <div className="lg:col-span-2 bg-gradient-to-br from-[#082753] to-[#04122A] p-8 lg:p-12 text-white flex flex-col justify-center">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFB400]/20 text-[#FFB400] text-xs font-semibold mb-4">
                  <Building2 className="w-3 h-3" />
                  Élu le 2 septembre 2023 | &quot;Gbôklè Renaissance&quot;
                </span>
                <h3 className="text-2xl lg:text-3xl font-bold mb-2">
                  L&apos;Honorable KÉBÉ Mahamadou
                </h3>
                <p className="text-white/80 text-lg">Président du Conseil Régional du Gbôklè</p>
                <p className="text-[#FFB400] text-sm mt-1">Député à l&apos;Assemblée Nationale | Promoteur du Forum</p>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[#FFB400]" />
                  <span>Homme d&apos;affaires & entrepreneur accompli</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[#FFB400]" />
                  <span>Directeur de campagne - Alassane Ouattara (2015)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[#FFB400]" />
                  <span>Député de Dakpadou-Sago (2021-2023)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[#FFB400]" />
                  <span>41,2% des suffrages aux élections régionales</span>
                </div>
              </div>
            </div>

            {/* President Content */}
            <div className="lg:col-span-3 p-8 lg:p-12">
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Visionnaire, homme de terrain et entrepreneur accompli, l&apos;Honorable KÉBÉ Mahamadou 
                place le développement des territoires et l&apos;humain au cœur de son action depuis 2023 
                à la tête du Conseil Régional du Gbôklè. C&apos;est fort de sa légitimité sur le terrain 
                et de sa vision du développement intégré qu&apos;il a pris l&apos;initiative de lancer ce Forum.
              </p>

              <h4 className="font-bold text-[#0F172A] mb-6 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#FFB400]" />
                Réalisations Concrètes depuis 2023
              </h4>

              <div className="grid sm:grid-cols-2 gap-4">
                {presidentAchievements.map((achievement, index) => (
                  <div key={index} className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#25A18E]/10 flex items-center justify-center">
                        <achievement.icon className="w-4 h-4 text-[#25A18E]" />
                      </div>
                      <h5 className="font-semibold text-[#0F172A] text-sm">{achievement.title}</h5>
                    </div>
                    <ul className="space-y-1">
                      {achievement.items.map((item, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                          <ChevronRight className="w-3 h-3 mt-0.5 text-[#25A18E] flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Quote */}
              <div className="mt-8 p-6 bg-[#082753]/5 rounded-xl border-l-4 border-[#082753]">
                <p className="text-[#0F172A] italic mb-3">
                  &quot;Notre territoire possède tous les atouts pour devenir une destination économique 
                  majeure de Côte d&apos;Ivoire et d&apos;Afrique de l&apos;Ouest. Ce forum sera le catalyseur qui 
                  transformera ce potentiel en réalité, pour le bénéfice de nos populations et la 
                  prospérité partagée avec nos partenaires investisseurs.&quot;
                </p>
                <p className="text-sm font-semibold text-[#082753]">
                  — Honorable KÉBÉ Mahamadou, Président du Conseil Régional du Gbôklè
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Commissaire General Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-5 gap-0">
            {/* Content First on Desktop */}
            <div className="lg:col-span-3 p-8 lg:p-12 order-2 lg:order-1">
              <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
                Commissariat Général
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">
                Pilotage Opérationnel d&apos;Excellence
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Nommé par l&apos;Honorable KÉBÉ Mahamadou, Ouagnimin SORHO a reçu la mission de piloter 
                l&apos;organisation opérationnelle du forum avec l&apos;exigence d&apos;un événement de niveau international. 
                À la tête d&apos;EKIP HOLDING COMPANY, il construit le premier écosystème de vie intégré 
                en Côte d&apos;Ivoire et en Afrique de l&apos;Ouest.
              </p>

              <h4 className="font-bold text-[#0F172A] mb-4">Missions du Commissariat :</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Coordination institutionnelle",
                  "Mobilisation des investisseurs",
                  "Partenariats financiers",
                  "Programmation & logistique",
                  "Communication & rayonnement",
                  "Lancement de l'ADEG"
                ].map((mission, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#25A18E]" />
                    <span className="text-sm text-[#0F172A]">{mission}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Commissaire Card */}
            <div className="lg:col-span-2 bg-gradient-to-br from-[#25A18E] to-[#1a7a6a] p-8 lg:p-12 text-white flex flex-col justify-center order-1 lg:order-2">
              <div className="w-20 h-20 rounded-full bg-white/10 mx-auto lg:mx-0 mb-6 flex items-center justify-center">
                <Briefcase className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold mb-2 text-center lg:text-left">
                Ouagnimin SORHO
              </h3>
              <p className="text-white/80 mb-1 text-center lg:text-left">Commissaire Général du Forum</p>
              <p className="text-[#FFB400] text-sm text-center lg:text-left">Fondateur & CEO, EKIP HOLDING COMPANY</p>
              
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#FFB400]" />
                  <span>Expertise mobilisation de capitaux</span>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#FFB400]" />
                  <span>Vision écosystémique</span>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#FFB400]" />
                  <span>Réseau institutionnel étendu</span>
                </div>
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#FFB400]" />
                  <span>Excellence opérationnelle</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sectors Section
const SectorsSection = () => {
  const sectors = [
    {
      id: "tourism",
      title: "Tourisme & Hôtellerie",
      subtitle: "Premier entrant, zéro concurrence",
      desc: "200 km de littoral vierge, plages & mangroves, patrimoine colonial unique. Resorts, éco-lodges et tourisme fluvial.",
      tri: "15-25%",
      tickets: "50M → 50 Mds FCFA",
      image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
      icon: Palmtree,
      color: "#082753"
    },
    {
      id: "fishing",
      title: "Pêche & Aquaculture",
      subtitle: "L'Or Bleu du Gbôklè",
      desc: "Eaux parmi les plus poissonneuses du Golfe de Guinée. Transformation, conserveries et aquaculture : valeur ×10 à ×20.",
      tri: "18-30%",
      tickets: "50M → 20 Mds FCFA",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80",
      icon: Fish,
      color: "#25A18E"
    },
    {
      id: "agriculture",
      title: "Agriculture & Agro-industrie",
      subtitle: "De la matière première à la valeur ajoutée",
      desc: "Cacao, anacarde, hévéa, manioc, palmier, riz. Le Port de San Pedro à 68 km pour un export facilité.",
      tri: "12-20%",
      tickets: "50M → 20 Mds FCFA",
      image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=80",
      icon: Leaf,
      color: "#FFB400"
    },
    {
      id: "training",
      title: "Formation Professionnelle",
      subtitle: "Demande garantie",
      desc: "48% de la population a moins de 17 ans. École hôtelière, métiers de la mer, agro-transformation et numérique.",
      tri: "10-18%",
      tickets: "50M → 2 Mds FCFA",
      image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80",
      icon: GraduationCap,
      color: "#082753"
    },
    {
      id: "environment",
      title: "Environnement & Développement Durable",
      subtitle: "La transition verte du Gbôklè",
      desc: "Solaire, hydroélectricité (3 barrages), biomasse, crédits carbone et écotourisme certifié. Impact ESG élevé.",
      tri: "8-15%",
      tickets: "100M → 30 Mds FCFA",
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&q=80",
      icon: Globe,
      color: "#25A18E"
    }
  ];

  return (
    <section id="sectors" className="section-padding bg-slate-50" data-testid="sectors-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Secteurs d&apos;Investissement
          </span>
          <h2 className="section-title">
            Cinq Secteurs d&apos;Excellence
          </h2>
          <p className="section-subtitle mx-auto">
            Un potentiel de plus de 50 milliards FCFA réparti sur cinq secteurs complémentaires,
            avec des rendements attractifs (TRI de 8 à 30%).
          </p>
        </div>

        <div className="sectors-grid">
          {sectors.map((sector, index) => (
            <div 
              key={sector.id}
              data-testid={`sector-card-${sector.id}`}
              className={`sector-card aspect-[3/4] ${index === 0 ? 'lg:col-span-2 lg:row-span-2 lg:aspect-auto' : ''}`}
            >
              <img 
                src={sector.image} 
                alt={sector.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="sector-card-content">
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${sector.color}30` }}
                  >
                    <sector.icon className="w-6 h-6" style={{ color: sector.color }} />
                  </div>
                  <span className="px-3 py-1 rounded-full bg-[#FFB400] text-[#0F172A] text-xs font-bold">
                    TRI {sector.tri}
                  </span>
                </div>
                <p className="text-sm text-white/70 font-medium mb-1">{sector.subtitle}</p>
                <h3 className="sector-card-title">{sector.title}</h3>
                <p className="sector-card-desc">{sector.desc}</p>
                <p className="text-xs text-[#FFB400] font-semibold mt-3">Ticket : {sector.tickets}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Program Section
const ProgramSection = () => {
  const days = [
    {
      day: "Jour 1",
      theme: "Ouverture, Plénière & Panels Sectoriels",
      date: "08h00 – 22h00",
      color: "bg-[#082753]",
      events: [
        "Cérémonie d'ouverture & allocutions officielles",
        "Conférence plénière : développement territorial & investissement privé",
        "Panel Tourisme : faire du Gbôklè une destination de référence",
        "Panel Pêche : moderniser la filière pour une croissance durable",
        "Panel Agriculture : de la production à la valorisation",
        "Networking, speed-dating investisseurs & dîner B2B"
      ]
    },
    {
      day: "Jour 2",
      theme: "Visites de Terrain & Nuit du Gbôklè",
      date: "Journée complète",
      color: "bg-[#25A18E]",
      events: [
        "Circuit A — Tourisme & Patrimoine (plages, phare de Sassandra)",
        "Circuit B — Pêche & Halieutique (débarcadère, aquaculture)",
        "Circuit C — Agriculture & Agro-industrie (cacao, hévéa)",
        "Rencontres B2B ciblées sur les sites",
        "Soirée culturelle « Nuit du Gbôklè » (18h-22h)",
        "Danses traditionnelles, musique, gastronomie & défilé de mode"
      ]
    },
    {
      day: "Jour 3",
      theme: "Engagements & Construction de l'Avenir",
      date: "Matin & Après-midi",
      color: "bg-[#FFB400]",
      events: [
        "Session plénière : annonces d'engagements",
        "Signatures de protocoles d'accord",
        "Lancement opérationnel de l'ADEG (guichet unique investisseur)",
        "Feuille de route & perspectives",
        "Cérémonie de clôture",
        "Conférence de presse"
      ]
    }
  ];

  return (
    <section id="program" className="section-padding bg-white" data-testid="program-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Programme du Forum
          </span>
          <h2 className="section-title">
            Trois Journées Intensives d&apos;Opportunités
          </h2>
          <p className="section-subtitle mx-auto">
            Un programme conçu pour maximiser les rencontres, les découvertes terrain 
            et les engagements concrets d&apos;investissement.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {days.map((day, index) => (
            <div 
              key={index}
              data-testid={`program-day-${index + 1}`}
              className="program-day-card group"
            >
              <div className={`program-day-badge ${day.color} text-white`}>
                <Calendar className="w-4 h-4" />
                {day.day}
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-2 group-hover:text-[#082753] transition-colors">
                {day.theme}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">{day.date}</p>
              
              <ul className="space-y-3">
                {day.events.map((event, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-[#25A18E] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#475569]">{event}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partnerships Section
const PartnershipsSection = () => {
  const partnerTypes = [
    {
      id: "investors",
      title: "Investisseurs",
      icon: TrendingUp,
      color: "bg-[#082753]",
      benefits: [
        "Portefeuille de projets présélectionnés",
        "Accompagnement personnalisé ADEG",
        "Mise en relation directe avec autorités",
        "Facilitations administratives",
        "Conditions fiscales attractives",
        "Garanties institutionnelles"
      ],
      packages: [
        { name: "VIP", condition: "≥ 5 milliards FCFA", perks: "3 participants, hébergement 5★, présentation plénière" },
        { name: "Premium", condition: "1-5 milliards FCFA", perks: "2 participants, stand offert, B2B organisés" },
        { name: "Standard", condition: "100M - 1 Md FCFA", perks: "Tarif réduit, accès complet, networking" }
      ],
      contact: "investisseurs@forumgbokle.org"
    },
    {
      id: "institutions",
      title: "Institutions Financières",
      icon: Building2,
      color: "bg-[#25A18E]",
      benefits: [
        "Exposition de vos offres de financement",
        "Participation aux panels stratégiques",
        "Stands d'exposition premium",
        "Deal-flow qualifié",
        "Visibilité maximale médias",
        "Partenariat institutionnel RSE/ESG"
      ],
      packages: [
        { name: "Majeur", condition: "≥ 10 milliards FCFA", perks: "Logo premium, stand 30m², 2 panels, 5 participants" },
        { name: "Partenaire", condition: "2-10 milliards FCFA", perks: "Logo, stand 15m², 1 panel, 3 participants" },
        { name: "Participant", condition: "Sans minimum", perks: "Stand payant, tarif réduit" }
      ],
      contact: "partenariats@forumgbokle.org"
    },
    {
      id: "sponsors",
      title: "Sponsors & Exposants",
      icon: Award,
      color: "bg-[#FFB400]",
      benefits: [
        "Exposition de vos solutions",
        "Networking avec décideurs",
        "Génération de leads",
        "Visibilité de marque",
        "Packages modulables"
      ],
      packages: [
        { name: "Diamant", condition: "20+ millions FCFA", perks: "Naming session, stand 50m², 10 participants" },
        { name: "Or", condition: "10-20 millions FCFA", perks: "Logo premium, stand 30m², 5 participants" },
        { name: "Argent", condition: "5-10 millions FCFA", perks: "Logo, stand 15m², 3 participants" }
      ],
      contact: "exposition@forumgbokle.org"
    },
    {
      id: "projects",
      title: "Porteurs de Projets Locaux",
      icon: Lightbulb,
      color: "bg-[#082753]",
      benefits: [
        "Accès aux investisseurs",
        "Visibilité maximale (stands, pitch)",
        "Accompagnement technique ADEG",
        "Mise en relation ciblée",
        "Accès au financement"
      ],
      packages: [
        { name: "Tarif réduit", condition: "25,000 FCFA", perks: "Accès complet, catalogue projets, coaching" }
      ],
      contact: "projets@forumgbokle.org"
    }
  ];

  const [activeTab, setActiveTab] = useState("investors");

  return (
    <section id="partnerships" className="section-padding bg-white" data-testid="partnerships-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Opportunités de Partenariat
          </span>
          <h2 className="section-title">
            Rejoignez-Nous dans Cette Transformation
          </h2>
          <p className="section-subtitle mx-auto">
            Plusieurs formules de participation adaptées à vos objectifs et votre niveau d&apos;engagement.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {partnerTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveTab(type.id)}
              data-testid={`tab-${type.id}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                activeTab === type.id
                  ? `${type.color} text-white shadow-lg`
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <type.icon className="w-4 h-4" />
              {type.title}
            </button>
          ))}
        </div>

        {/* Content */}
        {partnerTypes.map((type) => (
          <div
            key={type.id}
            className={`${activeTab === type.id ? "block" : "hidden"}`}
          >
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Benefits */}
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${type.color} flex items-center justify-center`}>
                    <type.icon className="w-5 h-5 text-white" />
                  </div>
                  Avantages {type.title}
                </h3>
                <ul className="space-y-3">
                  {type.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#25A18E] flex-shrink-0 mt-0.5" />
                      <span className="text-[#475569]">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Packages */}
              <div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-6">Packages Disponibles</h3>
                <div className="space-y-4">
                  {type.packages.map((pkg, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-[#0F172A]">{pkg.name}</span>
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${type.color} text-white`}>
                          {pkg.condition}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{pkg.perks}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-[#082753]/5 rounded-xl">
                  <p className="text-sm text-[#475569]">
                    <strong>Contact :</strong>{" "}
                    <a href={`mailto:${type.contact}`} className="text-[#082753] hover:underline">
                      {type.contact}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Practical Info Section
const PracticalInfoSection = () => {
  const infoCards = [
    {
      icon: Calendar,
      title: "Dates & Lieu",
      color: "bg-[#082753]",
      items: [
        { label: "Dates", value: "08 au 10 Octobre 2026" },
        { label: "Lieu", value: "Sassandra-Fresco, Région du Gbôklè" },
        { label: "Pays", value: "Côte d'Ivoire" }
      ]
    },
    {
      icon: CreditCard,
      title: "Tarifs Participants",
      color: "bg-[#25A18E]",
      items: [
        { label: "Entreprises", value: "150,000 FCFA" },
        { label: "Individuels", value: "75,000 FCFA" },
        { label: "Projets locaux", value: "25,000 FCFA" },
        { label: "Étudiants", value: "10,000 FCFA" }
      ]
    },
    {
      icon: Hotel,
      title: "Hébergement",
      color: "bg-[#FFB400]",
      items: [
        { label: "Hôtel 4★", value: "à partir de 35,000 FCFA/nuit" },
        { label: "Hôtel 3★", value: "à partir de 25,000 FCFA/nuit" },
        { label: "Résidence", value: "à partir de 15,000 FCFA/nuit" }
      ]
    },
    {
      icon: Car,
      title: "Transport",
      color: "bg-[#082753]",
      items: [
        { label: "Depuis Abidjan", value: "320 km (~4h30)" },
        { label: "Depuis San-Pedro", value: "80 km (~1h15)" },
        { label: "Navettes", value: "Incluses sur place" }
      ]
    }
  ];

  const services = [
    { icon: FileText, label: "Assistance visas" },
    { icon: Wifi, label: "Wifi haut débit" },
    { icon: Languages, label: "Traduction FR/EN" },
    { icon: Utensils, label: "Repas inclus" },
    { icon: Shield, label: "Sécurité renforcée" },
    { icon: Accessibility, label: "Accessibilité PMR" }
  ];

  return (
    <section id="practical" className="section-padding bg-slate-50" data-testid="practical-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Informations Pratiques
          </span>
          <h2 className="section-title">
            Préparez Votre Participation
          </h2>
        </div>

        {/* Info Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {infoCards.map((card, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-4`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-4">{card.title}</h3>
              <ul className="space-y-2">
                {card.items.map((item, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-muted-foreground">{item.label}:</span>{" "}
                    <span className="font-medium text-[#0F172A]">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Registration Steps */}
        <div className="bg-white rounded-2xl p-8 mb-12">
          <h3 className="text-xl font-bold text-[#0F172A] mb-6 text-center">Procédure d&apos;Inscription</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Formulaire", desc: "Remplissez le formulaire en ligne" },
              { step: "2", title: "Validation", desc: "Examen par le Commissariat" },
              { step: "3", title: "Confirmation", desc: "Réception de votre badge" },
              { step: "4", title: "Accès", desc: "Présentez votre badge le jour J" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#082753] text-white font-bold text-xl flex items-center justify-center mx-auto mb-3">
                  {item.step}
                </div>
                <h4 className="font-semibold text-[#0F172A] mb-1">{item.title}</h4>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Services */}
        <div className="text-center">
          <h3 className="font-bold text-[#0F172A] mb-6">Services aux Participants</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {services.map((service, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <service.icon className="w-4 h-4 text-[#25A18E]" />
                <span className="text-sm font-medium text-[#0F172A]">{service.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Resources Section
const ResourcesSection = () => {
  const documents = [
    { icon: FileText, title: "Dossier d'Investissement Régional", desc: "80 pages - Étude complète du territoire", size: "PDF" },
    { icon: Briefcase, title: "Fiches Projet par Secteur", desc: "4 documents - Portefeuilles de projets", size: "PDF" },
    { icon: BookOpen, title: "Guide de l'Investisseur", desc: "50 pages - Mode d'emploi pratique", size: "PDF" },
    { icon: Scale, title: "Cadre Réglementaire et Fiscal", desc: "40 pages - Code des investissements", size: "PDF" },
    { icon: ClipboardList, title: "Formulaire d'Inscription", desc: "Document à compléter", size: "PDF" },
    { icon: Map, title: "Carte Interactive du Gbôklè", desc: "Zones d'investissement", size: "PDF" }
  ];

  return (
    <section id="resources" className="section-padding bg-white" data-testid="resources-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Ressources
          </span>
          <h2 className="section-title">
            Documentation à Télécharger
          </h2>
          <p className="section-subtitle mx-auto">
            Accédez à notre documentation complète pour préparer votre investissement.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc, index) => (
            <div 
              key={index}
              className="group bg-slate-50 rounded-xl p-6 hover:bg-[#082753] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#082753]/10 group-hover:bg-white/20 flex items-center justify-center flex-shrink-0 transition-colors">
                  <doc.icon className="w-6 h-6 text-[#082753] group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#0F172A] group-hover:text-white mb-1 transition-colors">
                    {doc.title}
                  </h3>
                  <p className="text-sm text-muted-foreground group-hover:text-white/70 mb-2 transition-colors">
                    {doc.desc}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[#25A18E] group-hover:text-[#FFB400] transition-colors">
                    <Download className="w-3 h-3" />
                    {doc.size}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-muted-foreground mb-4">
            Certains documents nécessitent un enregistrement préalable.
          </p>
          <Button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
          >
            S&apos;inscrire pour accéder
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Press Section
const PressSection = () => {
  const pressReleases = [
    { title: "Le Gbôklè Lance son Premier Forum d'Investissement", type: "Communiqué officiel" },
    { title: "Pourquoi le Gbôklè ? Le Potentiel d'une Région d'Avenir", type: "Note de contexte" },
    { title: "150 km de plages : La nouvelle Riviera ivoirienne", type: "Communiqué Tourisme" },
    { title: "Le potentiel halieutique attire les investisseurs", type: "Communiqué Pêche" }
  ];

  const mediaAssets = [
    { icon: Image, label: "Photos HD", desc: "Paysages, activités, personnalités" },
    { icon: Video, label: "Vidéos", desc: "Film promotionnel, témoignages, drone" },
    { icon: FileText, label: "Logos", desc: "Tous formats disponibles" }
  ];

  return (
    <section id="press" className="section-padding bg-slate-50" data-testid="press-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
            Espace Presse
          </span>
          <h2 className="section-title">
            Informations Médias
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Press Releases */}
          <div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-6 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#082753]" />
              Dossier de Presse
            </h3>
            <div className="space-y-3">
              {pressReleases.map((release, i) => (
                <div key={i} className="bg-white rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer group">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium text-[#25A18E] uppercase">{release.type}</span>
                      <h4 className="font-medium text-[#0F172A] group-hover:text-[#082753] transition-colors">
                        {release.title}
                      </h4>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#082753] transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Media Gallery */}
          <div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-6 flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#082753]" />
              Galerie Médias
            </h3>
            <div className="space-y-4 mb-8">
              {mediaAssets.map((asset, i) => (
                <div key={i} className="bg-white rounded-xl p-5 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 rounded-xl bg-[#FFB400]/10 flex items-center justify-center">
                    <asset.icon className="w-6 h-6 text-[#FFB400]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">{asset.label}</h4>
                    <p className="text-sm text-muted-foreground">{asset.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Press Contact */}
            <div className="bg-[#082753] rounded-xl p-6 text-white">
              <h4 className="font-bold mb-4 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-[#FFB400]" />
                Accréditation Presse
              </h4>
              <p className="text-white/80 text-sm mb-4">
                Journalistes : demandez votre accréditation officielle pour couvrir le forum.
              </p>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#FFB400]" />
                  presse@forumgbokle.org
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#FFB400]" />
                  +225 05 06 06 55 06
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      full_name: "",
      email: "",
      organization: "",
      sector: "",
      participant_type: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, data);
      toast.success("Inscription réussie!", {
        description: "Nous vous contacterons prochainement avec les détails du forum.",
      });
      form.reset();
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur s'est produite. Veuillez réessayer.";
      toast.error("Erreur", { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sectors = [
    { value: "tourism", label: "Tourisme & Hôtellerie" },
    { value: "fishing", label: "Pêche & Aquaculture" },
    { value: "agriculture", label: "Agriculture & Agro-industrie" },
    { value: "training", label: "Formation Professionnelle" },
    { value: "environment", label: "Environnement & Développement Durable" },
    { value: "multiple", label: "Plusieurs secteurs" },
  ];

  const participantTypes = [
    { value: "investor", label: "Investisseur" },
    { value: "institution", label: "Institution Financière" },
    { value: "sponsor", label: "Sponsor / Exposant" },
    { value: "project_holder", label: "Porteur de Projet Local" },
    { value: "media", label: "Média / Presse" },
    { value: "other", label: "Autre" },
  ];

  return (
    <section id="contact" className="section-padding bg-slate-50" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Contact Info */}
          <div>
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-[#25A18E] mb-4">
              Nous Contacter
            </span>
            <h2 className="section-title mb-6">
              Rejoignez-Nous dans Cette Transformation
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              Inscrivez-vous dès maintenant pour participer au Forum &quot;Investir dans le Gbôklè&quot; 
              et découvrir les opportunités exceptionnelles de cette région.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#082753]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#082753]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Lieu du Forum</h4>
                  <p className="text-muted-foreground">Sassandra-Fresco, Région du Gbôklè<br/>Côte d&apos;Ivoire</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#25A18E]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#25A18E]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Email</h4>
                  <p className="text-muted-foreground">
                    <a href="mailto:contact@forumgbokle.org" className="hover:text-[#082753] transition-colors">contact@forumgbokle.org</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FFB400]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#FFB400]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Téléphone</h4>
                  <p className="text-muted-foreground">
                    <a href="tel:+2250506065506" className="hover:text-[#082753] transition-colors">+225 05 06 06 55 06</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-4">Suivez-nous</h4>
              <div className="flex gap-3">
                {socialLinks.map((s, i) => (
                  <a 
                    key={i} 
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`contact-social-${s.label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-muted-foreground hover:text-[#082753] hover:shadow-md transition-all"
                  >
                    <s.Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Registration Form */}
          <div className="contact-form" data-testid="registration-form">
            <h3 className="text-xl font-bold text-[#0F172A] mb-6">Formulaire d&apos;Inscription</h3>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom complet *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Votre nom complet" 
                          data-testid="input-full-name"
                          className="h-12"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input 
                          type="email"
                          placeholder="votre@email.com" 
                          data-testid="input-email"
                          className="h-12"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="organization"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisation / Entreprise *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Nom de votre organisation" 
                          data-testid="input-organization"
                          className="h-12"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid sm:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="participant_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Profil *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-participant-type" className="h-12">
                              <SelectValue placeholder="Sélectionnez" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {participantTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sector"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secteur d&apos;intérêt *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-sector" className="h-12">
                              <SelectValue placeholder="Sélectionnez" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {sectors.map((sector) => (
                              <SelectItem key={sector.value} value={sector.value}>
                                {sector.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message (optionnel)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Partagez vos attentes ou questions..."
                          data-testid="input-message"
                          className="min-h-[100px] resize-none"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  data-testid="submit-registration"
                  className="btn-primary w-full"
                >
                  {isSubmitting ? "Envoi en cours..." : "S'inscrire au Forum"}
                  {!isSubmitting && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const quickLinks = [
    { label: "À Propos", href: "#about" },
    { label: "Leadership", href: "#leadership" },
    { label: "Secteurs", href: "#sectors" },
    { label: "Programme", href: "#program" },
    { label: "Partenariats", href: "#partnerships" },
    { label: "Infos Pratiques", href: "#practical" },
    { label: "Ressources", href: "#resources" },
    { label: "Presse", href: "#press" },
  ];

  const contacts = [
    { label: "Investisseurs", email: "investisseurs@forumgbokle.org" },
    { label: "Partenariats", email: "partenariats@forumgbokle.org" },
    { label: "Exposants", email: "exposition@forumgbokle.org" },
    { label: "Presse", email: "presse@forumgbokle.org" },
  ];

  return (
    <footer className="footer" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/logo-forum-white.png"
              alt="Forum Investir dans le Gbôklè"
              data-testid="footer-logo"
              className="h-16 w-auto rounded-lg bg-white p-2 mb-5"
            />
            <p className="text-slate-400 mb-6">
              Votre porte d&apos;entrée vers la Riviera Atlantique Ivoirienne.
            </p>
            <a
              href="https://www.forumgbokle.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-website-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#FFB400] hover:text-white transition-colors mb-6"
            >
              <Globe className="w-4 h-4" />
              www.forumgbokle.org
            </a>
            <div className="flex gap-3">
              {socialLinks.map((s, i) => (
                <a 
                  key={i} 
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-[#FFB400] transition-all"
                >
                  <s.Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Liens Rapides</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="footer-link text-sm">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contacts Dédiés</h4>
            <ul className="space-y-3">
              {contacts.map((contact, i) => (
                <li key={i}>
                  <span className="text-sm text-slate-500 block">{contact.label}</span>
                  <a href={`mailto:${contact.email}`} className="footer-link text-sm">{contact.email}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Location */}
          <div>
            <h4 className="font-semibold text-white mb-4">Localisation</h4>
            <div className="text-slate-400 text-sm space-y-2">
              <p>Sassandra-Fresco</p>
              <p>Région du Gbôklè</p>
              <p>Côte d&apos;Ivoire</p>
            </div>
            <div className="mt-4 p-4 rounded-xl bg-white/5">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Sous le haut patronage du</p>
              <p className="text-sm text-white">Conseil Régional du Gbôklè</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            © 2026 Forum &quot;Investir dans le Gbôklè&quot; - Tous droits réservés
          </p>
          <p className="text-sm text-slate-500">
            Commissariat Général | Conseil Régional du Gbôklè
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Determine active section
      const sections = ["hero", "about", "leadership", "sectors", "program", "partnerships", "practical", "resources", "press", "contact"];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Navigation scrolled={scrolled} activeSection={activeSection} />
      <main>
        <HeroSection />
        <StatsSection />
        <LaunchCeremonySection />
        <AboutSection />
        <LeadershipSection />
        <SectorsSection />
        <ProgramSection />
        <PartnershipsSection />
        <PracticalInfoSection />
        <ResourcesSection />
        <PressSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
